import React from 'react'
import {
    Box,
    chakra,
    Text,
    VStack,
    Icon,
    Card,
    CardHeader,
    CardBody,
    Heading,
    SimpleGrid,
    useColorModeValue,
    Flex,
    Circle,
    Divider,
} from "@chakra-ui/react";
import { FiDollarSign, FiShield, FiTrendingUp } from "react-icons/fi";
import { BiWallet } from "react-icons/bi";

export default function Stake() {
    const cardBg = useColorModeValue('white', '#3e3d3c');
    const shadowColor = useColorModeValue('0 10px 20px rgba(0,0,0,.05)', '0 10px 20px rgba(0,0,0,.3)');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const hoverBg = useColorModeValue('gray.50', 'gray.700');
    const notesBg = useColorModeValue('gray.50', 'gray.700');
    
    const benefits = [
        { icon: FiTrendingUp, title: "18% APR", description: "Earn competitive returns on your TRX holdings" },
        { icon: FiShield, title: "Secure", description: "Your TRX never leaves your wallet" },
        { icon: BiWallet, title: "Direct Staking", description: "Stake directly from your wallet" },
        { icon: FiDollarSign, title: "Provider Revenue", description: "Become a Tronweb Rentals provider" }
    ];

    const steps = [
        {
            title: "Access Account Management",
            description: "Open wallet, click on profile icon at the right bottom, and then click \"Public Account Management\""
        },
        {
            title: "Navigate to Permissions",
            description: "Make sure you have selected the right wallet. Under wallet management click on \"Permission\""
        },
        {
            title: "Add Multi-signature",
            description: "Now you are in Multi-signature, click \"Add\" on the right top"
        },
        {
            title: "Set Permissions",
            description: "Click \"Add permissions\" first, and select \"Stake TRX\", \"Unstake TRX\" and \"Vote\" in the popup page. Then click \"Confirm\" at the bottom"
        },
        {
            title: "Configure Permission Details",
            description: "Back to the Add Permission Page. Input \"Permission name\" (e.g. Stake and Earn) and set other values as required"
        },
        {
            title: "Confirm Transaction",
            description: "Double check threshold, keys (address), weight, and click \"Confirm\". Transaction Fee: 100 TRX. Make sure your balance is greater than 100 TRX"
        },
        {
            title: "Verify Authorization",
            description: "After authorization succeeds! Click \"Permission\" again, you will see a new page listed. Now there are 3 dots (2 dots before authorization), move to the last dot to see the authorization details"
        }
    ];

    return (
        <Box pt={{ base: 3, md: 5 }} px={{ base: 3, sm: 4, md: 6, lg: 8 }} pb={{ base: 4, md: 0 }}>
            <VStack spacing={{ base: 6, md: 8 }} maxW="7xl" mx="auto">
                {/* Header Section */}
                <VStack spacing={{ base: 3, md: 4 }} textAlign="center" px={{ base: 2, md: 0 }}>
                    <chakra.h1
                        fontSize={{ base: "2xl", sm: "3xl", md: "4xl", lg: "5xl" }}
                        fontWeight="bold"
                        bgGradient="linear(to-r, #f70112, #ff4757)"
                        bgClip="text"
                        mb={{ base: 1, md: 2 }}
                        lineHeight={{ base: "1.2", md: "1.1" }}
                    >
                        Stake and Earn
                    </chakra.h1>
                    <Text
                        fontSize={{ base: "md", sm: "lg", md: "xl" }}
                        color={textColor}
                        maxW={{ base: "full", sm: "2xl", md: "3xl" }}
                        lineHeight={{ base: "1.5", md: "1.6" }}
                        px={{ base: 2, sm: 0 }}
                    >
                        Become a Tronweb Rentals provider and earn up to 18% APR on your TRX. 
                        Your TRX never leaves your wallet - stake directly from your own wallet.
                    </Text>
                </VStack>

                {/* Benefits Grid */}
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={{ base: 4, md: 6 }} w="full" px={{ base: 0, md: 0 }}>
                    {benefits.map((benefit, index) => (
                        <Card
                            key={index}
                            bg={cardBg}
                            boxShadow={shadowColor}
                            borderRadius={{ base: "lg", md: "xl" }}
                            p={{ base: 4, md: 6 }}
                            textAlign="center"
                            transition="transform 0.2s"
                            _hover={{ transform: { base: "none", md: "translateY(-2px)" } }}
                        >
                            <VStack spacing={{ base: 2, md: 3 }}>
                                <Box
                                    bg="red.50"
                                    p={{ base: 2, md: 3 }}
                                    borderRadius="full"
                                    color="#f70112"
                                >
                                    <Icon as={benefit.icon} boxSize={{ base: 5, md: 6 }} />
                                </Box>
                                <Heading size={{ base: "xs", md: "sm" }} color="#f70112">
                                    {benefit.title}
                                </Heading>
                                <Text fontSize={{ base: "xs", md: "sm" }} color={textColor} lineHeight={{ base: "1.4", md: "1.5" }}>
                                    {benefit.description}
                                </Text>
                            </VStack>
                        </Card>
                    ))}
                </SimpleGrid>

                {/* Setup Instructions */}
                <Card
                    bg={cardBg}
                    boxShadow={shadowColor}
                    borderRadius={{ base: "lg", md: "xl" }}
                    w="full"
                >
                    <CardHeader pb={{ base: 3, md: 4 }}>
                        <Heading size={{ base: "md", md: "lg" }} textAlign="center" color="#f70112">
                            Setup Instructions
                        </Heading>
                        <Text textAlign="center" mt={{ base: 1, md: 2 }} color={textColor} fontSize={{ base: "sm", md: "md" }}>
                            Follow these steps to enable staking from your wallet
                        </Text>
                    </CardHeader>
                    <CardBody pt={0}>
                        <VStack spacing={{ base: 0, md: 0 }} align="stretch">
                            {steps.map((step, index) => (
                                <Box key={index}>
                                    <Flex
                                        direction={{ base: "column", md: "row" }}
                                        align={{ base: "stretch", md: "flex-start" }}
                                        p={{ base: 4, md: 6 }}
                                        position="relative"
                                        _hover={{ bg: hoverBg }}
                                        transition="background-color 0.2s"
                                        borderRadius={{ base: "md", md: "lg" }}
                                    >
                                        {/* Step Number and Connector */}
                                        <Flex
                                            direction="column"
                                            align="center"
                                            mr={{ base: 0, md: 6 }}
                                            mb={{ base: 3, md: 0 }}
                                            position="relative"
                                        >
                                            <Circle
                                                size="40px"
                                                bg="red.500"
                                                color="white"
                                                fontWeight="bold"
                                                fontSize="md"
                                                position="relative"
                                                zIndex={2}
                                                boxShadow="0 4px 6px rgba(247, 1, 18, 0.3)"
                                            >
                                                {index + 1}
                                            </Circle>
                                            {index < steps.length - 1 && (
                                                <Box
                                                    display={{ base: "none", md: "block" }}
                                                    width="2px"
                                                    bg="red.200"
                                                    position="absolute"
                                                    top="40px"
                                                    bottom={{ base: "-100%", md: "-140%" }}
                                                    left="50%"
                                                    transform="translateX(-50%)"
                                                    zIndex={1}
                                                />
                                            )}
                                        </Flex>

                                        {/* Step Content */}
                                        <VStack align="stretch" flex="1" spacing={{ base: 2, md: 3 }}>
                                            <Heading 
                                                size={{ base: "sm", md: "md" }} 
                                                color="#f70112"
                                                lineHeight={{ base: "1.3", md: "1.4" }}
                                            >
                                                {step.title}
                                            </Heading>
                                            <Text 
                                                color={textColor} 
                                                fontSize={{ base: "sm", md: "md" }}
                                                lineHeight={{ base: "1.5", md: "1.6" }}
                                            >
                                                {step.description}
                                            </Text>
                                        </VStack>
                                    </Flex>
                                    
                                    {/* Mobile Divider */}
                                    {index < steps.length - 1 && (
                                        <Divider 
                                            display={{ base: "block", md: "none" }}
                                            borderColor="red.100"
                                            mx={4}
                                        />
                                    )}
                                </Box>
                            ))}
                        </VStack>

                        {/* Additional Note */}
                        <Box mt={{ base: 4, md: 6 }} p={{ base: 3, md: 4 }} bg={notesBg} borderRadius={{ base: "md", md: "lg" }}>
                            <Text fontSize={{ base: "xs", md: "sm" }} color={textColor} fontStyle="italic" lineHeight={{ base: "1.4", md: "1.5" }}>
                                <strong>Note:</strong> You can ignore the reward withdrawal section - this is handled automatically 
                                for vote reward withdrawals by the system.
                            </Text>
                        </Box>

                        {/* Important Notice */}
                        <Box mt={{ base: 4, md: 6 }} p={{ base: 3, md: 4 }} bg={useColorModeValue('blue.50', 'blue.900')} borderRadius={{ base: "md", md: "lg" }} border="1px" borderColor={useColorModeValue('blue.200', 'blue.700')}>
                            <Text fontSize={{ base: "xs", md: "sm" }} color={useColorModeValue('blue.800', 'blue.200')} fontWeight="semibold" lineHeight={{ base: "1.4", md: "1.5" }}>
                                <strong>Important!</strong> Make sure your wallet has more than 100 TRX for transaction fees. 
                                Your staked TRX remains in your wallet at all times.
                            </Text>
                        </Box>
                    </CardBody>
                </Card>
            </VStack>
            {/* Bottom padding */}
            <Box pb={{ base: 6, md: 8 }} />
        </Box>
    )
}
